<template>
    <div class="max-w-4xl mx-auto p-6 bg-slate-800 rounded-xl shadow-lg border border-slate-700">
      <h2 class="text-2xl font-bold mb-6 text-slate-100 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary-500"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="10" y1="13" x2="8" y2="13"/><line x1="10" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        UUID Generator
      </h2>
      
      <div class="space-y-6">
        <!-- UUID v4 -->
        <div class="p-6 bg-slate-900/50 rounded-lg border border-slate-700">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <h3 class="font-medium text-slate-300">Standard UUID (v4)</h3>
                <button @click="generateUUID" class="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors text-sm font-medium">Generate New</button>
            </div>
            <div v-if="uuid" class="flex items-center gap-2">
                <code class="flex-1 block w-full p-4 bg-slate-950 rounded-lg font-mono text-primary-300 break-all border border-slate-800">{{ uuid }}</code>
                <button @click="copyToClipboard(uuid)" class="p-4 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg border border-slate-700 transition-colors" title="Copy">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                </button>
            </div>
        </div>

        <!-- Short UUID -->
        <div class="p-6 bg-slate-900/50 rounded-lg border border-slate-700">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <h3 class="font-medium text-slate-300">Short UUID (Segment)</h3>
                <button @click="generateShortUUID" class="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm font-medium">Generate New</button>
            </div>
            <div v-if="shortUuid" class="flex items-center gap-2">
                <code class="flex-1 block w-full p-4 bg-slate-950 rounded-lg font-mono text-primary-300 break-all border border-slate-800">{{ shortUuid }}</code>
                <button @click="copyToClipboard(shortUuid)" class="p-4 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg border border-slate-700 transition-colors" title="Copy">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                </button>
            </div>
        </div>
      </div>

      <!-- Snackbar -->
      <transition enter-active-class="transition ease-out duration-300" enter-from-class="transform translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100" leave-active-class="transition ease-in duration-200" leave-from-class="transform translate-y-0 opacity-100" leave-to-class="transform translate-y-4 opacity-0">
        <div v-if="showSnackbar" class="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-xl border border-slate-700 flex items-center gap-2 z-50">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-400"><polyline points="20 6 9 17 4 12"/></svg>
            Copied to clipboard!
        </div>
      </transition>
    </div>
  </template>
  
  <script lang="ts">
  import { defineComponent, ref } from 'vue';
  import { v4 as uuidv4 } from 'uuid';
  
  export default defineComponent({
    name: 'UUIDGenerator',
    setup() {
      const uuid = ref('');
      const shortUuid = ref('');
      const showSnackbar = ref(false);
  
      const generateUUID = () => {
        uuid.value = uuidv4();
      };
  
      const generateShortUUID = () => {
        shortUuid.value = uuidv4().split('-')[0];
      };
  
      const copyToClipboard = async (text: string) => {
        try {
          await navigator.clipboard.writeText(text);
          showSnackbar.value = true;
          setTimeout(() => { showSnackbar.value = false; }, 3000); // Hide after 3 seconds
        } catch (err) {
          console.error('Failed to copy text: ', err);
        }
      };
  
      return { uuid, shortUuid, showSnackbar, generateUUID, generateShortUUID, copyToClipboard };
    }
  });
  </script>
  
  <style scoped>
/* Tailwind handles most styles now */
</style>
  